<template>
  <v-container fluid fill-height class="login-page">
    <v-layout align-center justify-center>
      <v-flex xs12 sm8 md5 lg4 xl3 class="login-flex">
        <v-card
          class="login-card"
          style="box-shadow: rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px, rgba(10, 37, 64, 0.35) 0px -2px 6px 0px inset;"
        >
          <!-- ── Header ── -->
          <div class="login-header">
            <div class="top-title-group">
              <span class="top-label">National University - EMS</span>
              <span class="top-title">College login (Admin)</span>
            </div>
            <div class="logo-badge-wrapper">
              <div class="logo-badge">
                <v-img
                  :src="require('@/assets/nu_logos/nu_logo_r.png')"
                  contain
                  width="110"
                  height="110"
                ></v-img>
              </div>
            </div>
          </div>

          <!-- Progress bar placeholder -->
          <div style="height: 3px;"></div>

          <!-- ── Form body ── -->
          <v-card-text class="login-body">
            <!-- Server error alert -->
            <v-alert
              v-if="errorMessage"
              type="error"
              dismissible
              dense
              class="mb-4 error-alert"
              @input="errorMessage = ''"
              >{{ errorMessage }}</v-alert
            >

            <v-form
              ref="loginForm"
              @submit.prevent="checkUser"
              style="margin-top: 30px;"
            >
              <!-- College Code -->
              <div class="field-label">COLLEGE CODE</div>
              <div
                class="input-wrapper mb-1"
                :class="{ 'input-wrapper--error': fieldErrors.username }"
              >
                <v-icon size="18" color="#9CA3AF" class="input-icon"
                  >person_outline</v-icon
                >
                <input
                  v-model="user.username"
                  name="username"
                  type="text"
                  placeholder="Enter your college code"
                  class="nu-input"
                  @keyup.enter="checkUser"
                  @input="clearFieldError('username')"
                />
              </div>
              <div v-if="fieldErrors.username" class="field-error mb-3">
                <v-icon size="13" color="#E53935" class="field-error-icon"
                  >error_outline</v-icon
                >
                {{ fieldErrors.username }}
              </div>
              <div v-else class="mb-3"></div>

              <!-- Password -->
              <div class="field-label">PASSWORD</div>
              <div
                class="input-wrapper mb-1"
                :class="{ 'input-wrapper--error': fieldErrors.password }"
              >
                <v-icon size="18" color="#9CA3AF" class="input-icon"
                  >lock_outline</v-icon
                >
                <input
                  v-model="user.password"
                  id="password"
                  name="password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Enter your password"
                  class="nu-input"
                  @keyup.enter="checkUser"
                  @input="clearFieldError('password')"
                />
                <v-icon
                  size="18"
                  color="#9CA3AF"
                  class="input-icon-append"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  @click="showPassword = !showPassword"
                  >{{ showPassword ? "visibility_off" : "visibility" }}</v-icon
                >
              </div>
              <div v-if="fieldErrors.password" class="field-error mb-3">
                <v-icon size="13" color="#E53935" class="field-error-icon"
                  >error_outline</v-icon
                >
                {{ fieldErrors.password }}
              </div>
              <div v-else class="mb-3"></div>

              <!-- Security Verification -->
              <div class="field-label">SECURITY VERIFICATION</div>
              <div
                class="captcha-row"
                :class="{ 'captcha-row--error': fieldErrors.captcha }"
              >
                <div class="captcha-image-box">
                  <img
                    v-if="captcha.img"
                    :src="captcha.img"
                    class="captcha-img"
                    alt="Math captcha"
                  />
                  <span v-else class="captcha-placeholder">Loading...</span>
                </div>

                <span class="captcha-equals">=</span>

                <div
                  class="input-wrapper captcha-input-wrapper"
                  :class="{ 'input-wrapper--error': fieldErrors.captcha }"
                >
                  <input
                    v-model="user.captcha"
                    placeholder="Answer"
                    type="text"
                    inputmode="numeric"
                    class="nu-input"
                    aria-label="Enter the answer to the math problem"
                    @keyup.enter="checkUser"
                    @input="clearFieldError('captcha')"
                  />
                </div>

                <v-btn
                  fab
                  small
                  depressed
                  color="primary"
                  class="captcha-refresh-btn"
                  @click="loadCaptcha"
                  aria-label="Refresh captcha"
                  title="Refresh captcha"
                >
                  <v-icon size="18">refresh</v-icon>
                </v-btn>
              </div>
              <div v-if="fieldErrors.captcha" class="field-error mt-1 mb-1">
                <v-icon size="13" color="#E53935" class="field-error-icon"
                  >error_outline</v-icon
                >
                {{ fieldErrors.captcha }}
              </div>

              <!-- Hint box -->
              <div
                class="hint-box"
                :class="{
                  'mt-2': fieldErrors.captcha,
                  'mt-3': !fieldErrors.captcha,
                }"
              >
                <v-icon size="16" color="#3F51B5" class="hint-icon"
                  >info_outline</v-icon
                >
                <span class="hint-text">
                  <strong>Hint:</strong> Solve the math problem to prove you are
                  human and complete login.
                </span>
              </div>
            </v-form>
          </v-card-text>

          <v-card-actions class="login-actions">
            <v-layout column align-center style="width:100%;">
              <v-btn
                color="primary"
                block
                depressed
                large
                class="login-btn white--text"
                :loading="loading"
                @click="checkUser"
              >
                <v-icon left size="20">account_circle</v-icon>
                LOGIN
              </v-btn>
              <span class="forgot-link mt-3" @click="goForgetPassword"
                >Forget Password?</span
              >
            </v-layout>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
    <v-snackbar
      v-model="snackbar"
      top
      right
      :timeout="6000"
      color="red darken-2"
      content-class="custom-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3" color="white">
          error_outline
        </v-icon>

        <div class="snackbar-message">
          <span v-if="errorLogMsg">{{ errorLogMsg.message }}</span> <br />
          <!-- <span v-if="errorMessage">{{ errorMessage }} </span> -->
        </div>
      </div>

      <v-btn slot="action" icon dark @click="snackbar = false">
        <v-icon>close</v-icon>
      </v-btn>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      snackbar: false,
      expansion: true,
      errorMessage: "",
      showPassword: false,
      loading: false,
      user: {
        password: "",
        username: "",
        captcha: "",
        key: "",
      },
      fieldErrors: {
        username: "",
        password: "",
        captcha: "",
      },
    };
  },
  computed: {
    captcha() {
      return this.$store.getters.getCaptcha;
    },
    errorLogMsg() {
      return this.$store.getters.getErrorLog || {};
    },
  },
  watch: {
    errorLogMsg(val) {
      if (val && val.message) {
        this.snackbar = true;
      }
    },
  },
  methods: {
    clearFieldError(field) {
      this.fieldErrors[field] = "";
    },

    validate() {
      // Reset all field errors
      this.fieldErrors = { username: "", password: "", captcha: "" };
      let valid = true;

      if (!this.user.username.trim()) {
        this.fieldErrors.username = "College code is required.";
        valid = false;
      }

      if (!this.user.password.trim()) {
        this.fieldErrors.password = "Password is required.";
        valid = false;
      }

      if (!this.user.captcha.trim()) {
        this.fieldErrors.captcha = "Security Verification is required.";
        valid = false;
      }

      return valid;
    },

    loadCaptcha() {
      this.$store.dispatch("loadCaptcha").then((data) => {
        this.user.key = data.key;
        this.user.captcha = "";
        this.fieldErrors.captcha = "";
      });
    },

    goStart() {
      this.$router.push("/");
    },

    goHome() {
      this.$router.push("/college/dashboard");
    },

    goForgetPassword() {
      this.$router.push("/c-forget-password");
    },

    checkUser() {
      this.errorMessage = "";

      if (!this.validate()) return;

      this.loading = true;

      this.$store
        .dispatch("authenticateCollege", this.user)
        .then(() => {
          this.$router.push("/college/dashboard");
        })
        .catch((err) => {
          // Show server error in the alert banner; also refresh captcha
          this.errorMessage =
            (err &&
              err.response &&
              err.response.data &&
              err.response.data.message) ||
            "Invalid credentials. Please check your college code and password.";
          this.user.password = "";
          this.loadCaptcha();
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },

  mounted() {
    localStorage.clear();
    this.loadCaptcha();
  },
};
</script>

<style scoped>
/* ════════════════════════════════════
   Design tokens
   ════════════════════════════════════ */
.login-page {
  --brand: #3f51b5;
  --brand-light: rgba(63, 81, 181, 0.1);
  --bg-page: #e8eaf2;
  --bg-field: #f9fafb;
  --border: #e5e7eb;
  --border-hover: #9ca3af;
  --border-error: #e53935;
  --label: #6b7280;
  --hint-bg: #eef0fb;
  --badge-size: 106px;
  --card-px: 28px;
  --body-pt: 20px;
  --body-pb: 4px;
  --actions-px: 28px;
  --actions-pb: 24px;

  background-color: var(--bg-page);
  min-height: 100vh;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
}

/* ════════════════════════════════════
   Layout
   ════════════════════════════════════ */
.login-flex {
  max-width: 460px;
  width: 100%;
  margin: 0 auto;
  padding: 0 12px;
}

/* ════════════════════════════════════
   Card
   ════════════════════════════════════ */
.login-card {
  border-radius: 16px !important;
  overflow: visible !important;
  width: 100%;
}

/* ════════════════════════════════════
   Header
   ════════════════════════════════════ */
.login-header {
  background-color: var(--brand);
  border-radius: 16px 16px 0 0;
  padding: 28px var(--card-px);
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 88px;
}

.top-title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: -10px;
}

.top-label {
  font-size: 10px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
  letter-spacing: 1.2px;
  text-transform: uppercase;
}

.top-title {
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

/* ════════════════════════════════════
   Logo badge
   ════════════════════════════════════ */
.logo-badge-wrapper {
  position: absolute;
  top: 14px;
  right: 20px;
  z-index: 2;
}

.logo-badge {
  width: var(--badge-size);
  height: var(--badge-size);
  border-radius: 50%;
  background: #ebebeb;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--brand);
  box-shadow: 0 0 0 3px #fff, 0 4px 16px rgba(63, 81, 181, 0.25);
  overflow: hidden;
  flex-shrink: 0;
}

/* ════════════════════════════════════
   Form body
   ════════════════════════════════════ */
.login-body {
  padding: var(--body-pt) var(--card-px) var(--body-pb) !important;
}

/* ════════════════════════════════════
   Field labels
   ════════════════════════════════════ */
.field-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.7px;
  color: var(--label);
  text-transform: uppercase;
  margin-bottom: 6px;
}

/* ════════════════════════════════════
   Inline field error
   ════════════════════════════════════ */
.field-error {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #e53935;
  line-height: 1.4;
}

.field-error-icon {
  flex-shrink: 0;
}

/* ════════════════════════════════════
   Custom input wrapper
   ════════════════════════════════════ */
.input-wrapper {
  display: flex;
  align-items: center;
  background: var(--bg-field);
  border: 1.5px solid var(--border);
  border-radius: 10px;
  padding: 0 12px;
  height: 44px;
  transition: border-color 0.15s, box-shadow 0.15s;
  box-sizing: border-box;
  width: 100%;
}

.input-wrapper:hover {
  border-color: var(--border-hover);
}

.input-wrapper:focus-within {
  border-color: var(--brand);
  border-width: 2px;
  box-shadow: 0 0 0 3px var(--brand-light);
}

.input-wrapper--error {
  border-color: var(--border-error) !important;
  border-width: 1.5px !important;
  box-shadow: none !important;
}

.input-wrapper--error:focus-within {
  border-color: var(--border-error) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.12) !important;
}

.input-icon {
  flex-shrink: 0;
  margin-right: 8px;
  color: #9ca3af;
  pointer-events: none;
}

.input-icon-append {
  flex-shrink: 0;
  margin-left: 6px;
  color: #9ca3af;
  cursor: pointer;
}

.nu-input {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: clamp(13px, 3.5vw, 14px);
  color: #111827;
  font-family: inherit;
  height: 100%;
  -webkit-appearance: none;
  appearance: none;
  box-shadow: none;
}

.nu-input::placeholder {
  color: #9ca3af;
  font-size: clamp(12.5px, 3vw, 13.5px);
}

/* ════════════════════════════════════
   Error alert
   ════════════════════════════════════ */
.error-alert {
  border-radius: 8px !important;
  font-size: 13px !important;
}

/* ════════════════════════════════════
   Captcha row
   ════════════════════════════════════ */
.captcha-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  flex-wrap: nowrap;
}

.captcha-image-box {
  flex: 0 0 auto;
  width: clamp(120px, 42%, 170px);
  height: 44px;
  border-radius: 10px;
  border: 1.5px solid var(--border);
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.captcha-row--error .captcha-image-box {
  border-color: var(--border-error);
}

.captcha-img {
  max-width: 100%;
  height: 52px;
  object-fit: contain;
}

.captcha-placeholder {
  font-size: 11px;
  color: #9ca3af;
}

.captcha-equals {
  font-size: clamp(20px, 5vw, 24px);
  color: #6b7280;
  font-weight: 300;
  flex-shrink: 0;
  user-select: none;
}

.captcha-input-wrapper {
  flex: 1 1 0 !important;
  min-width: 0;
}

.captcha-refresh-btn {
  flex-shrink: 0 !important;
  width: 42px !important;
  height: 42px !important;
  border-radius: 50% !important;
}

/* ════════════════════════════════════
   Hint box
   ════════════════════════════════════ */
.hint-box {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: var(--hint-bg);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 2px;
}

.hint-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

.hint-text {
  font-size: clamp(11.5px, 3vw, 12.5px);
  color: #374151;
  line-height: 1.55;
}

.hint-text strong {
  color: var(--brand);
  font-weight: 600;
}

/* ════════════════════════════════════
   Actions
   ════════════════════════════════════ */
.login-actions {
  padding: 16px var(--actions-px) var(--actions-pb) !important;
}

.login-btn {
  height: clamp(44px, 11vw, 50px) !important;
  border-radius: 10px !important;
  font-size: clamp(13px, 3.5vw, 14px) !important;
  font-weight: 600 !important;
  letter-spacing: 1.2px !important;
  text-transform: uppercase !important;
  width: 100% !important;
  padding: 12px !important;
}

.forgot-link {
  font-size: 13px;
  color: var(--brand);
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.15s;
}

.forgot-link:hover {
  opacity: 1;
  text-decoration: underline;
}

/* ════════════════════════════════════
   BREAKPOINTS
   ════════════════════════════════════ */
@media (max-width: 374px) {
  .login-page {
    --badge-size: 80px;
    --card-px: 14px;
    --actions-px: 14px;
    --actions-pb: 18px;
  }
  .login-flex {
    padding-left: 6px;
    padding-right: 6px;
  }
  .login-header {
    min-height: 72px;
    padding: 20px 14px;
  }
  .logo-badge-wrapper {
    top: 10px;
    right: 12px;
  }
  .captcha-image-box {
    width: 100px;
    height: 38px;
  }
  .captcha-refresh-btn {
    width: 36px !important;
    height: 36px !important;
  }
  .captcha-row {
    gap: 6px;
  }
}

@media (min-width: 375px) and (max-width: 479px) {
  .login-page {
    --badge-size: 92px;
    --card-px: 18px;
    --actions-px: 18px;
  }
  .login-header {
    min-height: 78px;
    padding: 22px 18px;
  }
  .captcha-image-box {
    width: 118px;
  }
}

@media (min-width: 480px) and (max-width: 599px) {
  .login-page {
    --badge-size: 100px;
    --card-px: 22px;
    --actions-px: 22px;
  }
}

@media (min-width: 600px) and (max-width: 839px) {
  .login-flex {
    padding-left: 24px;
    padding-right: 24px;
  }
  .login-page {
    --card-px: 32px;
    --actions-px: 32px;
    --actions-pb: 28px;
  }
}

@media (min-width: 840px) {
  .login-page {
    --card-px: 36px;
    --actions-px: 36px;
  }
}

@media (min-width: 1920px) {
  .login-page {
    --badge-size: 116px;
    --card-px: 40px;
    --actions-px: 40px;
    --actions-pb: 32px;
  }
  .login-flex {
    max-width: 500px;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .login-page {
    --body-pt: 12px;
    --actions-pb: 14px;
    --badge-size: 72px;
  }
  .login-header {
    min-height: 60px;
    padding: 14px var(--card-px);
  }
  .logo-badge-wrapper {
    top: 6px;
    right: 16px;
  }
  .hint-box {
    padding: 7px 10px;
  }
}

@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .logo-badge {
    border-width: 3px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .input-wrapper {
    transition: none !important;
  }
}
</style>
